import EthereumAccountAvatar from '#/components/EthereumAccountAvatar';
import { Button } from '#/components/ui/button';
import { Input } from '#/components/ui/input';
import { useTranslation } from '#/contexts/useTranslation';
import { useAppKit } from '@reown/appkit/react';
import React, {
  ChangeEventHandler,
  ComponentProps,
  MutableRefObject,
  useCallback,
} from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { getAddress, isAddress } from 'viem';
import { useAccount } from 'wagmi';

interface Props
  extends ControllerRenderProps,
    Omit<
      ComponentProps<'input'>,
      'name' | 'value' | 'onChange' | 'onBlur' | 'ref'
    > {
  useConnectedWallet?: boolean;
  isManualSetRef?: MutableRefObject<Set<string>>;
}

export const AddressInput = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      useConnectedWallet = false,
      isManualSetRef,
      onChange,
      onBlur,
      value,
      name,
      ...props
    },
    ref
  ) => {
    const translation = useTranslation(
      translation => translation.EthereumStaking.StakingAction.Information
    );
    const trySetAddress = useCallback(
      (address?: string) => {
        if (address != null && isAddress(address)) {
          // auto set
          isManualSetRef?.current.delete(name);
          onChange(getAddress(address));
        } else {
          onChange('');
        }
      },
      [isManualSetRef, name, onChange]
    );

    const { isConnected, address } = useAccount();
    const modal = useAppKit();

    const onUseConnectedWallet = async () => {
      if (!isConnected) {
        modal.open({ view: 'Connect' });
      } else {
        trySetAddress(address);
      }
    };
    const onInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
      event => {
        // manual set
        isManualSetRef?.current.add(name);
        onChange(event);
      },
      [isManualSetRef, name, onChange]
    );

    return (
      <div className="flex items-center gap-2">
        <div className="relative flex-auto">
          <div className="absolute inset-y-0 left-px flex items-center pl-3 pointer-events-none">
            <EthereumAccountAvatar
              address={value}
              diameter={24}
              className="w-6 h-6"
            />
          </div>
          <Input
            type="text"
            className="w-full pl-11"
            name={name}
            value={value}
            onChange={onInputChange}
            onBlur={onBlur}
            {...props}
            ref={ref}
          />
        </div>
        {useConnectedWallet && (
          <div className="shrink-0">
            <Button
              variant="outline"
              type="button"
              onClick={onUseConnectedWallet}
              className="uppercase"
            >
              {translation.UseConnectedWalletButtonLabel}
            </Button>
          </div>
        )}
      </div>
    );
  }
);

AddressInput.displayName = 'AddressInput';
